<template>
  <div v-if="NOTARIA">
    <!-- Cabecera: la de Servicios en una notaría; minotaria.es no la tiene -->
    <!-- Plantillas 2 a 5: cabecera de página de la plantilla -->
    <N2CabezaPagina v-if="!acceso.esAgregador.value && disenoPropio" :titulo="titulo" :imagen="banner.Imagen" :filtro="banner.ImagenFiltro" :rastro="[{ texto: t('Trámites online'), to: getLocalePath('/tramites') }, { texto: titulo }]" />
    <SliderBannerNotinTwo v-else-if="!acceso.esAgregador.value" :obj="banner" type="servicios" />

    <div :class="acceso.esAgregador.value ? 'tramite-seo-fondo tramite-seo-fondo--portal' : 'tramite-seo-fondo rn-section-gap bg_color--1'">
      <v-container>
        <v-row>
          <v-col cols="12" md="10" lg="8" class="mx-auto">
            <article class="tramite-seo">
              <nav class="tramite-seo__migas" aria-label="breadcrumb">
                <NuxtLink :to="getLocalePath('/tramites')">{{ t('Todos los trámites') }}</NuxtLink>
              </nav>

              <h1 v-if="acceso.esAgregador.value" class="tramite-seo__titulo">{{ titulo }}</h1>
              <p v-if="!acceso.esAgregador.value && ciudad" class="tramite-seo__lugar">{{ NOTARIA.Nombre }} · {{ ciudad }}</p>

              <p class="tramite-seo__intro">{{ intro }}</p>

              <!-- Trámites que se pueden firmar por videoconferencia (minotaria, o la notaría la ofrece). Ref: https://trello.com/c/dc7kKk4R -->
              <p v-if="tipo && videoconferenciaEn(tipo.codigo)" class="tramite-seo__video">
                <v-icon size="22">mdi-video-outline</v-icon>
                <span>{{ acceso.esAgregador.value
                  ? t('Este trámite se puede firmar por videoconferencia en muchos casos, sin desplazarse. Puede elegir cualquier notaría de España que lo ofrezca.')
                  : t('Este trámite se puede firmar por videoconferencia en muchos casos, sin desplazarse a la notaría.') }}</span>
              </p>

              <section v-if="documentos.length" class="tramite-seo__bloque">
                <h2>{{ t('Para empezar, tenga a mano:') }}</h2>
                <ul class="tramite-seo__docs">
                  <li v-for="d in documentos" :key="d.nombre">
                    <v-icon size="18">mdi-file-document-outline</v-icon>
                    <span>{{ d.nombre }}<em v-if="!d.obligatorio"> · {{ t('Si procede') }}</em></span>
                  </li>
                </ul>
                <p class="tramite-seo__nota">{{ t('Puede adjuntarlos en el encargo online o aportarlos más adelante.') }}</p>
              </section>

              <section v-if="contenido" class="tramite-seo__bloque">
                <h2>{{ t('Cómo funciona') }}</h2>
                <ol class="tramite-seo__pasos">
                  <li v-for="p in contenido.pasos" :key="p">{{ p }}</li>
                </ol>
              </section>

              <section v-if="contenido" class="tramite-seo__bloque">
                <h2>{{ t('Preguntas frecuentes') }}</h2>
                <div class="tramite-seo__faq">
                  <details v-for="f in contenido.faqs" :key="f.p">
                    <summary>{{ f.p }}</summary>
                    <p>{{ f.r }}</p>
                  </details>
                </div>
              </section>

              <div class="tramite-seo__cta">
                <div>
                  <p class="tramite-seo__cta-titulo">{{ t('¿Quiere hacer este encargo con nosotros?') }}</p>
                  <p class="tramite-seo__cta-texto">{{ t('Envíenos sus datos y la documentación por internet y le contactaremos para prepararlo.') }}</p>
                </div>
                <NuxtLink class="tramite-seo__boton" :to="acceso.enlace('seo', tramite!.codigo)">{{ t('Hacerlo online') }}</NuxtLink>
              </div>

              <p class="tramite-seo__aviso">{{ t('Información general. La notaría revisará su caso concreto.') }}</p>
            </article>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script setup lang="ts">
import N2CabezaPagina from '../../components/notaria2/N2CabezaPagina.vue'
import { useDisenoPropio } from '../../composables/useNotaria2'
const disenoPropio = useDisenoPropio()
import { computed } from 'vue'
import { useStore } from '../../store/index'
import { useAccesoSolicitud } from '../../composables/solicitudAcceso'
import { useSolicitudWeb } from '../../composables/useSolicitudWeb'
import { useSeoUrls, recortar } from '../../composables/useSeoUrls'
import { t } from '../../composables/solicitudTextos'
import { tramiteSeoPorSlug, IDIOMAS_CONTENIDO_TRAMITE } from '../../data/tramitesSeo'
import { useUtils } from '#imports'

// Página informativa de un trámite (/tramites/herencia…), para buscadores y asistentes con IA.
// Ref: https://trello.com/c/p3o83iNG. Solo existe si el encargo online está activo y la notaría las ha activado (minotaria.es, siempre).
const store = useStore()
const { NOTARIA } = storeToRefs(store)
const route = useRoute()
const acceso = useAccesoSolicitud()
const seo = useSeoUrls()
const { getLocalePath } = useUtils()
const { tipos, cargarCatalogo, videoconferenciaEn } = useSolicitudWeb()

const tramite = tramiteSeoPorSlug(String(route.params.slug))
if (!tramite || !acceso.paginasTramite.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
}

// El catálogo (traducido y con la documentación de la notaría) se pide en el servidor para que salga en el HTML
await cargarCatalogo()

const tipo = computed(() => tipos.value.find((x: any) => x.codigo === tramite.codigo))
const documentos = computed(() => (tipo.value?.documentos ?? []) as { nombre: string; obligatorio: boolean }[])
// Texto redactado: solo en los idiomas que lo tienen. En los demás se enseña lo del catálogo y no se indexa
const contenido = tramite.contenido[seo.idioma] ?? null
const titulo = contenido?.titulo ?? tipo.value?.nombre ?? ''
const intro = contenido?.intro ?? tipo.value?.descripcion ?? ''
const ciudad = NOTARIA.value?.Contacto?.Municipio || ''

// Cabecera con el estilo de la sección Servicios de la notaría y el título de esta página
const banner = computed(() => {
  const base: any = NOTARIA.value?.Theme?.Servicios ?? {}
  return { ...base, Titulo: { ...base.Titulo, Texto: titulo }, Subtitulo: { ...base.Subtitulo, Texto: '' }, ShowButton: false }
})

const rutasPorIdioma = Object.fromEntries(
  seo.idiomas.filter((l) => IDIOMAS_CONTENIDO_TRAMITE.includes(l)).map((l) => [l, `/tramites/${tramite.slug}`]))
const lugar = [NOTARIA.value?.Nombre, ciudad].filter(Boolean).join(', ')

useMetaTags(toRaw(NOTARIA.value), 'Tramites', tramite.slug, {
  title: titulo,
  description: `${recortar(intro, 120)} — ${lugar}`,
  nombre: tipo.value?.nombre || titulo,
  seccion: t('Trámites online'),
  schema: 'Service',
  faqs: contenido?.faqs,
  noindex: !contenido,
  rutasPorIdioma
})
</script>

<style lang="scss">
.tramite-seo-fondo--portal { padding: 48px 0 72px; }
.tramite-seo {
  &__migas { margin-bottom: 20px; font-size: 0.9rem; a { color: inherit; opacity: 0.75; } }
  &__titulo { margin: 0 0 16px; }
  &__lugar { margin: 0 0 8px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; font-size: 0.85rem; color: rgb(var(--v-theme-primary)); }
  &__intro { font-size: 1.1rem; line-height: 1.65; margin-bottom: 8px; }
  &__video { display: flex; align-items: flex-start; gap: 10px; margin: 20px 0 0; padding: 14px 18px; border-radius: 12px;
    background: #eef4f9; color: #1f3b52; line-height: 1.5; .v-icon { color: rgb(var(--v-theme-primary)) !important; flex: none; } }
  &__bloque { margin-top: 36px; h2 { margin-bottom: 14px; } }
  &__docs { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px;
    li { display: flex; align-items: flex-start; gap: 8px; line-height: 1.45; .v-icon { color: rgb(var(--v-theme-primary)) !important; margin-top: 2px; } }
    em { font-style: normal; opacity: 0.7; font-size: 0.9em; } }
  &__nota { margin: 12px 0 0; font-size: 0.9rem; opacity: 0.75; }
  &__pasos { padding-left: 1.3em; display: grid; gap: 10px; line-height: 1.55; }
  &__faq details { border-bottom: 1px solid #e3e7ea; padding: 14px 0;
    summary { cursor: pointer; font-weight: 600; }
    p { margin: 10px 0 0; line-height: 1.6; } }
  &__cta { margin-top: 44px; display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 16px;
    padding: 24px 28px; border-radius: 14px; background: #f5f7f9; border: 1px solid #e3e7ea; color: #1d2329; }
  &__cta-titulo { font-size: 1.15rem; font-weight: 600; margin: 0 0 4px; }
  &__cta-texto { margin: 0; color: #5f6b76; }
  &__boton { display: inline-flex; align-items: center; min-height: 48px; padding: 0 26px; border-radius: 999px;
    background: rgb(var(--v-theme-primary)); color: rgb(var(--v-theme-on-primary)) !important;
    font-weight: 600; text-decoration: none !important; white-space: nowrap;
    &:hover, &:focus-visible { filter: brightness(1.08); } }
  &__aviso { margin-top: 20px; font-size: 0.85rem; opacity: 0.7; }
}
</style>
